import { SESSION_STORAGE_KEY, LOCAL_STORAGE_KEY, VISIT_DEBOUNCE_MS } from '/bb/constants.js';
import { snapshotForStorage, hydrateFromStorage } from '/bb/state.js';

const DAY_MS = 86400000;

/** Defensive read of sessionStorage; returns parsed state object or null. */
export function readSessionSnapshot() {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const raw = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    return hydrateFromStorage(JSON.parse(raw));
  } catch (e) {
    return null;
  }
}

export function writeSessionSnapshot(s) {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(snapshotForStorage(s)));
  } catch (e) {
    /* quota / disabled / private mode → ignore */
  }
}

export function clearSessionSnapshot() {
  try {
    if (typeof sessionStorage === 'undefined') return;
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
}

/**
 * Lifetime visit counters live in localStorage. Shape:
 *   { pages: { "<pageKey>": { visit_count, last_visit_at, forms: { "<formId>": { ... } } } } }
 */
export function readLifetime() {
  try {
    if (typeof localStorage === 'undefined') return { pages: {} };
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return { pages: {} };
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return { pages: {} };
    if (!parsed.pages || typeof parsed.pages !== 'object') parsed.pages = {};
    return parsed;
  } catch (e) {
    return { pages: {} };
  }
}

export function writeLifetime(lifetime) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(lifetime));
  } catch (e) {
    /* ignore */
  }
}

export function clearLifetime() {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(LOCAL_STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
}

export function priorSessionCount() {
  const lt = readLifetime();
  const sessions = lt.sessions;
  return sessions && Number.isFinite(sessions.count) ? sessions.count : 0;
}

export function bumpSessionLifetime(now) {
  const lt = readLifetime();
  const sessions = lt.sessions || { count: 0, last_started_at: 0 };
  const startedDay = dayBucket(now);
  const lastStartedDay = Number.isFinite(sessions.last_started_day)
    ? sessions.last_started_day
    : dayBucket(sessions.last_started_at);
  const priorActiveDays = Number.isFinite(sessions.active_days_count)
    ? sessions.active_days_count
    : sessions.count > 0 && Number.isFinite(lastStartedDay)
      ? 1
      : 0;

  sessions.count = (sessions.count || 0) + 1;
  if (!Number.isFinite(lastStartedDay)) {
    sessions.active_days_count = 1;
  } else if (startedDay > lastStartedDay) {
    sessions.active_days_count = priorActiveDays + 1;
  } else {
    sessions.active_days_count = priorActiveDays;
  }
  sessions.last_started_at = now;
  sessions.last_started_day = Number.isFinite(startedDay) ? startedDay : lastStartedDay;
  lt.sessions = sessions;
  writeLifetime(lt);
  return {
    count: sessions.count,
    activeDaysCount: sessions.active_days_count || 0,
  };
}

function dayBucket(value) {
  return Number.isFinite(value) && value > 0 ? Math.floor(value / DAY_MS) : null;
}

/**
 * Bump page visit if not within the debounce window. Returns the resulting visit count.
 */
export function bumpPageLifetime(pageKey, now) {
  const lt = readLifetime();
  const page = lt.pages[pageKey] || { visit_count: 0, last_visit_at: 0, forms: {} };
  if (!page.forms || typeof page.forms !== 'object') page.forms = {};
  if (now - (page.last_visit_at || 0) >= VISIT_DEBOUNCE_MS) {
    page.visit_count = (page.visit_count || 0) + 1;
    page.last_visit_at = now;
  }
  lt.pages[pageKey] = page;
  writeLifetime(lt);
  return page.visit_count;
}

/**
 * Bump form visit if not within the debounce window. Returns the resulting visit count.
 */
export function bumpFormLifetime(pageKey, formId, now) {
  const lt = readLifetime();
  const page = lt.pages[pageKey] || { visit_count: 0, last_visit_at: 0, forms: {} };
  if (!page.forms || typeof page.forms !== 'object') page.forms = {};
  const form = page.forms[formId] || { visit_count: 0, last_visit_at: 0 };
  if (now - (form.last_visit_at || 0) >= VISIT_DEBOUNCE_MS) {
    form.visit_count = (form.visit_count || 0) + 1;
    form.last_visit_at = now;
  }
  page.forms[formId] = form;
  lt.pages[pageKey] = page;
  writeLifetime(lt);
  return form.visit_count;
}

/** Read lifetime counts at hydration time (no mutation). */
export function lifetimeFor(pageKey, formId) {
  const lt = readLifetime();
  const page = lt.pages[pageKey];
  if (!page) return { page: 0, form: 0 };
  const form = page.forms && page.forms[formId];
  return { page: page.visit_count || 0, form: (form && form.visit_count) || 0 };
}
